import { createFileRoute } from '@tanstack/react-router';
import { Loader2, RefreshCw } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { useDemoOrgStatus } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute('/_app/admin/demo-org')({
    component: DemoOrgPage,
    head: () => ({
        meta: [
            {
                title: 'Demo Org | Phoebe Admin',
            },
        ],
    }),
});

function DemoOrgPage() {
    const { data: status, isLoading, mutate } = useDemoOrgStatus();
    const [isResetting, setIsResetting] = useState(false);

    const handleReset = async () => {
        setIsResetting(true);
        try {
            await getApiClient().post({
                url: '/admin/demo-org/reset',
                body: {},
                reason: 'admin-demo-org-reset',
            });
            toast.success('Demo org reset successfully.', {
                persistNotification: false,
            });
            await mutate();
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : 'Failed to reset demo org.';
            toast.error(message, { persistNotification: false });
        } finally {
            setIsResetting(false);
        }
    };

    const exists =
        status?.organization_id !== null &&
        status?.organization_id !== undefined;

    return (
        <div className="flex flex-col p-8">
            <div className="mb-8">
                <h1 className="text-foreground text-2xl font-semibold">
                    Demo Org
                </h1>
                <p className="text-muted-foreground mt-2 text-sm">
                    Create or reset the shared Phoebe demo organization.
                    Live-mode setup (org, admin membership, sandbox Twilio phone
                    number) is preserved across resets; only sandbox caregivers
                    get wiped and re-seeded.
                </p>
            </div>
            <div className="grid max-w-2xl gap-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Current state</CardTitle>
                        <CardDescription>
                            Read from the live database.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {isLoading ? (
                            <div className="text-muted-foreground flex items-center gap-2 text-sm">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading status…
                            </div>
                        ) : (
                            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
                                <dt className="text-muted-foreground">
                                    Organization
                                </dt>
                                <dd className="font-mono">
                                    {exists
                                        ? `${status?.organization_name} (${status?.organization_id})`
                                        : 'Not created'}
                                </dd>
                                <dt className="text-muted-foreground">
                                    team@phoebe.work admin
                                </dt>
                                <dd>
                                    {status?.admin_member_linked
                                        ? 'Linked'
                                        : 'Not linked'}
                                </dd>
                                <dt className="text-muted-foreground">
                                    Sandbox phone number
                                </dt>
                                <dd className="font-mono">
                                    {status?.sandbox_phone_number ??
                                        'Not configured'}
                                </dd>
                                <dt className="text-muted-foreground">
                                    Sandbox caregivers
                                </dt>
                                <dd>{status?.sandbox_caregiver_count ?? 0}</dd>
                            </dl>
                        )}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle>
                            {exists ? 'Reset to base' : 'Create demo org'}
                        </CardTitle>
                        <CardDescription>
                            {exists
                                ? 'Wipes sandbox caregivers for the demo org and re-seeds 20 fresh ones.'
                                : 'Creates the demo org, links team@phoebe.work as admin, configures the shared sandbox Twilio number, and seeds 20 sandbox caregivers.'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="flex justify-end">
                            <Button
                                onClick={handleReset}
                                disabled={isResetting}
                            >
                                {isResetting ? (
                                    <React.Fragment>
                                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                        Running…
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        {exists
                                            ? 'Reset to base'
                                            : 'Create demo org'}
                                    </React.Fragment>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
